/** Verifix integratsiyasi (backend: apps/integrations). Faqat owner. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Paginated } from "@/lib/api";

export interface SyncRun {
  id: number;
  kind: string;
  kind_label: string;
  started_at: string;
  finished_at: string | null;
  ok: boolean;
  triggered_by: string | null;
  stats: Record<string, unknown>;
  errors: { error?: string; status?: number | null }[];
  message: string;
}

export interface VerifixStatus {
  enabled: boolean;
  configured: boolean;
  filial_id: string;
  client_id_masked: string;
  sync_employees: boolean;
  sync_salaries: boolean;
  push_bonuses: boolean;
  sync_attendance: boolean;
  last_success_at: string | null;
  last_error: string;
  last_error_at: string | null;
  employees_count: number;
  unmatched_divisions: number;
  unmatched_employees: number;
  last_run: { kind: string; ok: boolean; started_at: string; message: string; stats: Record<string, unknown> } | null;
  version: number;
}

export type VerifixPatch = Partial<Pick<VerifixStatus, "enabled" | "filial_id" | "sync_employees" | "sync_salaries" | "push_bonuses" | "sync_attendance">> & { version: number };

export interface Mapping {
  id: number;
  kind: "division" | "employee";
  external_id: string;
  external_name: string;
  branch: number | null;
  branch_name: string | null;
  user: number | null;
  user_name: string | null;
  auto: boolean;
  active: boolean;
  matched: boolean;
}

export type VerifixAction = "test" | "sync-employees" | "pull-salaries" | "push-bonuses";

const BASE = "/api/integrations/verifix";
export const verifixKeys = { status: ["verifix", "status"] as const, runs: ["verifix", "runs"] as const, mappings: ["verifix", "mappings"] as const };

export function useVerifixStatus(enabled = true) {
  return useQuery({ queryKey: verifixKeys.status, queryFn: () => api.get<VerifixStatus>(`${BASE}/status/`), enabled, staleTime: 15_000 });
}

export function useUpdateVerifix() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: VerifixPatch) => api.patch<VerifixStatus>(`${BASE}/status/`, data),
    onSuccess: (data) => qc.setQueryData(verifixKeys.status, data),
  });
}

export function useVerifixAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, body }: { action: VerifixAction; body?: Record<string, unknown> }) =>
      api.post<{ run: SyncRun; status: VerifixStatus }>(`${BASE}/actions/${action}/`, body ?? {}),
    onSuccess: (data) => {
      qc.setQueryData(verifixKeys.status, data.status);
      qc.invalidateQueries({ queryKey: ["verifix"] });
    },
  });
}

export function useVerifixRuns(query: Record<string, string | number | boolean | undefined> = {}, enabled = true) {
  return useQuery({
    queryKey: [...verifixKeys.runs, query],
    queryFn: () => api.get<Paginated<SyncRun>>(`${BASE}/runs/`, { query: { page_size: 5, ...query } }),
    enabled,
  });
}

export function useVerifixMappings(kind: "division" | "employee", enabled = true) {
  return useQuery({
    queryKey: [...verifixKeys.mappings, kind],
    queryFn: () => api.get<Mapping[]>(`${BASE}/mappings/`, { query: { kind, active: true } }),
    enabled,
  });
}

export function useUpdateMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; branch?: number | null; user?: number | null }) =>
      api.patch<Mapping>(`${BASE}/mappings/${id}/`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["verifix"] }),
  });
}

// ---- Google akkaunt (OAuth). Faqat owner. Token hech qachon frontend'ga kelmaydi. ----

export type GoogleConnectionStatus = "disconnected" | "connected" | "needs_reauth";

export interface GoogleStatus {
  configured: boolean;
  status: GoogleConnectionStatus;
  email: string;
  name: string;
  can_read_sheets: boolean;
  connected_by: string | null;
  connected_at: string | null;
  last_used_at: string | null;
  last_error: string;
  last_error_at: string | null;
  redirect_uri: string;
  client_id_masked: string;
  version: number;
}

const GOOGLE = "/api/integrations/google";
// WebSocket "google" hodisasi shu kalitni yangilaydi (boshqa oyna ulasa yoki uzsa)
export const googleKeys = { status: ["google", "status"] as const };

export function useGoogleStatus(enabled = true) {
  return useQuery({ queryKey: googleKeys.status, queryFn: () => api.get<GoogleStatus>(`${GOOGLE}/`), enabled });
}

/** Google rozilik sahifasi URL'ini oladi; chaqiruvchi brauzerni o'sha yerga yuboradi */
export function useGoogleConnect() {
  return useMutation({ mutationFn: () => api.post<{ auth_url: string }>(`${GOOGLE}/connect/`) });
}

export function useGoogleDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<GoogleStatus>(`${GOOGLE}/disconnect/`),
    onSuccess: (data) => qc.setQueryData(googleKeys.status, data),
  });
}

// ---- Google Sheets nusxasi: platformadagi holat asl jadval ko'rinishida (backend: integrations/sheets_*). Faqat owner. ----

export interface SheetsStatus {
  configured: boolean;
  service_account_email: string;
  spreadsheet_id: string;
  url: string;
  title: string;
  enabled: boolean;
  last_synced_at: string | null;
  last_stats: { title: string; rows: number }[];
  last_error: string;
  last_error_at: string | null;
  updated_by: string | null;
  version: number;
}

const SHEETS = "/api/integrations/sheets";
// WebSocket "sheets_export" hodisasi shu kalitni yangilaydi (fon vazifasi yozganda ham)
export const sheetsKeys = { status: ["sheets_export", "status"] as const };
/** .xlsx -- oddiy havola (sessiya cookie'si bilan), brauzer faylni o'zi yuklaydi */
export const sheetsDownloadUrl = `${SHEETS}/download/`;

export function useSheetsStatus(enabled = true) {
  return useQuery({ queryKey: sheetsKeys.status, queryFn: () => api.get<SheetsStatus>(`${SHEETS}/`), enabled });
}

export function useUpdateSheets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { spreadsheet?: string; enabled?: boolean; version: number }) => api.patch<SheetsStatus>(`${SHEETS}/`, data),
    onSuccess: (data) => qc.setQueryData(sheetsKeys.status, data),
    onError: () => qc.invalidateQueries({ queryKey: sheetsKeys.status }),
  });
}

export function useSyncSheets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<SheetsStatus>(`${SHEETS}/sync/`),
    onSuccess: (data) => qc.setQueryData(sheetsKeys.status, data),
    onError: () => qc.invalidateQueries({ queryKey: sheetsKeys.status }),
  });
}
